import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { checkSession, clearSession, getUser, updateActivity } from '../../utils/session';
import { AdminOverlaysProvider } from '../admin/overlays';
import { Button, Modal, Spinner } from '../admin/ui';
import SiteCredit from '../SiteCredit';
import { IconClose, IconLink, IconLogout, IconMenu } from '../icons';
import { getMerchantPageMeta, merchantNavFor } from './navigation';

const MerchantContext = createContext(null);
export const useMerchantSession = () => useContext(MerchantContext);

/** This panel's own session only — the admin console keeps its own. */
const endMerchantSession = () => clearSession('merchant');

/**
 * Shell for every /merchant/* portal route.
 *
 * The guard checks the role, not merely that a token is valid: an admin token
 * is perfectly authentic and must still not open the merchant portal.
 */
const MerchantLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const performLogout = useCallback(() => {
    endMerchantSession();
    navigate('/merchant/login', { replace: true });
  }, [navigate]);

  /** Confirmed here so both the header and the mobile menu are covered. */
  const requestLogout = useCallback(() => setLogoutOpen(true), []);

  useEffect(() => {
    if (!checkSession('merchant')) {
      endMerchantSession();
      navigate('/merchant/login', { replace: true });
      return undefined;
    }

    const stored = getUser('merchant');
    if (!stored || stored.role !== 'merchant') {
      endMerchantSession();
      navigate('/merchant/login', { replace: true });
      return undefined;
    }
    setUser(stored);
    updateActivity('merchant');

    let cancelled = false;
    api
      .get('/api/auth/verify')
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.user?.role !== 'merchant') {
          endMerchantSession();
          navigate('/merchant/login', { replace: true });
          return;
        }
        setUser((current) => ({ ...current, ...data.user }));
        setVerifying(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Only a genuine auth rejection ends the session — see the note in
        // AdminLayout. A rate-limit response or a restarting API must not look
        // like an expired token.
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          endMerchantSession();
          navigate('/merchant/login', { replace: true });
          return;
        }
        setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // A token that expires mid-session must land back on the login screen.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          endMerchantSession();
          navigate('/merchant/login', { replace: true });
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, [navigate]);

  useEffect(() => {
    const onActivity = () => updateActivity('merchant');
    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);
    return () => {
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div className="flex items-center gap-3 text-sm font-medium text-ink-500">
          <Spinner className="h-5 w-5 text-brand-700" />
          Loading your portal…
        </div>
      </div>
    );
  }

  const meta = getMerchantPageMeta(location.pathname);
  const navItems = merchantNavFor(user?.merchantRole);
  const initials = (user?.name || user?.email || 'M')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <MerchantContext.Provider value={user}>
      <AdminOverlaysProvider>
        <div className="min-h-screen bg-ink-50">
          {/* Header */}
          <header className="sticky top-0 z-40 border-b border-ink-200 bg-white">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-20 items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <img src="/brand.png" alt="ParcelBuddy" className="h-12 w-auto" />
                  <nav className="hidden items-center gap-1 md:flex" aria-label="Portal">
                    {navItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                            isActive
                              ? 'bg-brand-50 font-600 text-brand-800'
                              : 'font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                          }`
                        }
                      >
                        <item.icon size={17} />
                        {item.label}
                      </NavLink>
                    ))}
                  </nav>
                </div>

                <div className="flex items-center gap-2">
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Opens the public site in a new tab"
                    className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900 sm:inline-flex"
                  >
                    <IconLink size={15} />
                    View public site
                  </a>

                  <div className="hidden items-center gap-2.5 border-l border-ink-200 pl-3 sm:flex">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-800 font-display text-[11px] font-700 text-white">
                      {initials}
                    </span>
                    <span className="min-w-0">
                      <span className="block max-w-[10rem] truncate text-[13px] font-600 leading-tight text-ink-900">
                        {user?.name}
                      </span>
                      <span className="block text-[11px] capitalize leading-tight text-ink-500">
                        {user?.merchantRole || 'agent'}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={requestLogout}
                      aria-label="Log out"
                      title="Log out"
                      className="rounded-lg p-2 text-ink-500 transition-colors hover:bg-danger-50 hover:text-danger-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    >
                      <IconLogout size={17} />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setMobileNavOpen((open) => !open)}
                    aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={mobileNavOpen}
                    className="-mr-1 rounded-lg p-2 text-ink-600 transition-colors hover:bg-ink-100 md:hidden"
                  >
                    {mobileNavOpen ? <IconClose size={20} /> : <IconMenu size={20} />}
                  </button>
                </div>
              </div>
            </div>

            {mobileNavOpen && (
              <div className="animate-rise border-t border-ink-200 bg-white md:hidden">
                <nav className="mx-auto w-full max-w-6xl space-y-1 px-4 py-3 sm:px-6">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                          isActive
                            ? 'bg-brand-50 font-600 text-brand-800'
                            : 'font-medium text-ink-700 hover:bg-ink-100'
                        }`
                      }
                    >
                      <item.icon size={17} />
                      {item.label}
                    </NavLink>
                  ))}
                  <button
                    type="button"
                    onClick={requestLogout}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-danger-700 transition-colors hover:bg-danger-50"
                  >
                    <IconLogout size={17} />
                    Log out
                  </button>
                </nav>
              </div>
            )}
          </header>

          {/* Page heading + content */}
          <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <div className="mb-6">
              <h1 className="font-display text-2xl font-800 tracking-tight text-ink-900">
                {meta.title}
              </h1>
              <p className="mt-1 text-sm text-ink-500">{meta.description}</p>
            </div>
            <Outlet />
          </main>

          <footer className="border-t border-ink-200 bg-white">
            <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-4 sm:px-6 lg:px-8">
              <p className="text-xs text-ink-500">
                ParcelBuddy · © {new Date().getFullYear()}
              </p>
              <SiteCredit />
            </div>
          </footer>
        </div>

        <Modal
          open={logoutOpen}
          onClose={() => setLogoutOpen(false)}
          title="Sign out?"
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setLogoutOpen(false)}>
                Stay signed in
              </Button>
              <Button variant="danger" onClick={performLogout}>
                Sign out
              </Button>
            </>
          }
        >
          <p className="text-sm leading-relaxed text-ink-600">
            You&apos;ll need to sign in again to get back into your portal. Any unsaved changes on
            this page will be lost.
          </p>
        </Modal>
      </AdminOverlaysProvider>
    </MerchantContext.Provider>
  );
};

export default MerchantLayout;
